/**
 * Internationalization system.
 * All player-facing strings go through t() — no hardcoded strings in UI code.
 *
 * Supports:
 * - String interpolation: t('ui.hud.year', { year: 5, week: 23 }) → "Year 5 - Week 23"
 * - Simple pluralization: t('staff.count', { count: 1 }) → "1 Developer" / "5 Developers"
 * - Graceful fallback: missing keys return the key itself
 */

let currentStrings: Record<string, string> = {};

/**
 * Load a strings dictionary (from theme's strings.json).
 */
export function loadStrings(strings: Record<string, string>): void {
  currentStrings = { ...strings };
}

/**
 * Add or override strings (for merging theme + locale overrides).
 */
export function mergeStrings(strings: Record<string, string>): void {
  Object.assign(currentStrings, strings);
}

/**
 * Translate a string key with optional parameter interpolation.
 *
 * @param key - The string key (e.g., 'ui.hud.year')
 * @param params - Optional parameters for interpolation
 * @returns The translated string, or the key itself if not found
 */
export function t(key: string, params?: Record<string, string | number>): string {
  let template = currentStrings[key];

  if (template === undefined) {
    // Graceful fallback: return the key
    return key;
  }

  if (!params) return template;

  // Simple pluralization: if params.count exists, check for plural form
  if ('count' in params) {
    const count = Number(params['count']);
    const pluralKey = `${key}_plural`;
    if (count !== 1 && currentStrings[pluralKey]) {
      template = currentStrings[pluralKey]!;
    }
  }

  // Interpolate {paramName} placeholders
  return template.replace(/\{(\w+)\}/g, (_match, paramName: string) => {
    const value = params[paramName];
    if (value === undefined) return `{${paramName}}`;
    return String(value);
  });
}

/**
 * Format a number according to locale conventions.
 * Uses Intl.NumberFormat for proper thousand separators, etc.
 */
export function formatNumber(value: number, locale: string = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Format a currency value.
 */
export function formatCurrency(
  value: number,
  symbol: string = '$',
  locale: string = 'en-US',
): string {
  const formatted = new Intl.NumberFormat(locale).format(Math.abs(value));
  return value < 0 ? `-${symbol}${formatted}` : `${symbol}${formatted}`;
}

/**
 * Get all loaded string keys (for validation/testing).
 */
export function getLoadedKeys(): string[] {
  return Object.keys(currentStrings);
}

/**
 * Check if a string key exists.
 */
export function hasKey(key: string): boolean {
  return key in currentStrings;
}
